import type { JSX } from 'react';
import { styled } from '@mui/material';
import {
  DataGridPremium,
  DataGridPremiumProps,
  GridRowModel,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from './toolbarSlotProps';

const StyledDataGridPremium = styled(DataGridPremium)(() => ({
  '& .MuiDataGrid-columnHeader': {
    background: 'rgba(255, 255, 255, 0.1)',
    height: '40px !important',
    padding: '0px 16px 0px 16px',
    '&.MuiDataGrid-columnHeaderCheckbox': {
      padding: '0px',
    },
    '& .MuiDataGrid-sortButton': {
      backgroundColor: 'transparent',
    },
  },
  // Columns scroll under the pinned headers, so these need an opaque base
  // under the same tint.
  '& .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedRight':
    {
      background:
        'linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), var(--DataGrid-t-cell-background-pinned)',
    },
  '& .MuiDataGrid-cell': {
    padding: '0px 16px 0px 16px',
  },
  // The filler cell before the right-pinned columns must stay as narrow as
  // MUI sizes it, or the pinned cells drift from their headers on scroll.
  '& .MuiDataGrid-cellEmpty': {
    padding: 0,
  },
  // Replicate legacy hidden-xs/hidden-sm behavior for responsive column visibility
  '& .hidden-xs, & .hidden-sm': {
    display: 'none',
  },
  '@media (min-width: 600px)': {
    '& .hidden-sm': {
      display: 'none',
    },
  },
  '@media (min-width: 960px)': {
    '& .hidden-xs, & .hidden-sm': {
      display: '',
    },
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: 14,
  },
  // Keep grid body scrollable but prevent footer from showing a scrollbar
  '& .MuiDataGrid-footerContainer': {
    overflow: 'hidden',
  },
  '& .MuiDataGrid-footerContainer .MuiDataGrid-scrollbar': {
    display: 'none',
  },
  '& .MuiDataGrid-footerContainer .MuiDataGrid-scrollbarFiller': {
    display: 'none',
  },
  border: 'none',
}));

const TypedStyledDataGridPremium = StyledDataGridPremium as <
  T extends GridRowModel,
>(
  props: DataGridPremiumProps<T>,
) => JSX.Element;

interface CustomDataTableProps<
  T extends GridRowModel,
> extends DataGridPremiumProps<T> {
  rows: ReadonlyArray<T>;
  columns: DataGridPremiumProps<T>['columns'];
}

const CustomDataTable = <T extends GridRowModel>({
  rows,
  columns,
  slotProps,
  ...props
}: CustomDataTableProps<T>) => {
  const mergedSlotProps: typeof slotProps = {
    ...slotProps,
    toolbar: {
      ...slotProps?.toolbar,
      ...defaultToolbarExportSlotProps,
      printOptions: {
        ...defaultToolbarExportSlotProps.printOptions,
        ...slotProps?.toolbar?.printOptions,
        disableToolbarButton: true,
      },
      csvOptions: {
        ...defaultToolbarExportSlotProps.csvOptions,
        ...slotProps?.toolbar?.csvOptions,
        disableToolbarButton: false,
      },
      excelOptions: {
        ...defaultToolbarExportSlotProps.excelOptions,
        ...slotProps?.toolbar?.excelOptions,
        disableToolbarButton: false,
      },
    },
  };

  return (
    <TypedStyledDataGridPremium<T>
      rows={rows}
      columns={columns}
      columnHeaderHeight={40}
      disableColumnResize={false}
      showColumnVerticalBorder={false}
      showCellVerticalBorder={false}
      // Enable sorting, filtering, menus, pinning unless caller disables
      disableColumnMenu={false}
      disableColumnFilter={false}
      disableColumnPinning={false}
      sortingOrder={['asc', 'desc', null]}
      // headerFilters={true}
      // headerFilters={false}
      hideFooter={true}
      slotProps={mergedSlotProps}
      {...props}
    />
  );
};

export default CustomDataTable;
